<template>
  <div>
    <div v-if="isLoading" class="spinner-container">
      <Spinner />
    </div>
    <div v-else>
      <div v-if="loanDetails.isParentLOC">
        <StudentForm
          ref="studentFormRef"
          :loanDetails="loanDetails"
          :draw="selectedDraw"
          :student="loanDetails?.student"
          @studentUpdated="emit('studentUpdated', $event)"
          :permissionIds="[Permissions.Draws]"
          :permissionLevel="PermissionLevels.Update"
        />
      </div>
      <div class="select-buttons-container">
        <button
          v-for="draw in draws"
          :key="draw.id"
          :class="['icon-button', { secondary: draw != selectedDraw }]"
          @click="selectDraw(draw)"
        >
          <i class="icon icon-money"></i>
          <div class="text-container">
            <span class="main-text">{{ draw.requestedAmountFormatted }}</span>
            <span class="subtext">Sequence #{{ draw.sequenceNumber }}</span>
          </div>
        </button>
        <PermissionsWrapper
          :permissionIds="[Permissions.Draws]"
          :permissionLevel="PermissionLevels.Create"
        >
          <button
            class="icon-button dashed"
            v-if="mode !== 'edit'"
            :disabled="canAddDraw"
            @click="changeMode('add')"
          >
            <span class="icon">+</span>
            <div class="text-container">
              <span class="main-text">Add New</span>
            </div>
          </button>
        </PermissionsWrapper>
      </div>

      <div class="card">
        <PermissionsWrapper
          :permissionIds="[Permissions.Draws]"
          :permissionLevel="PermissionLevels.Update"
        >
          <div class="action-button-container">
            <button
              v-if="canEditDraw"
              class="danger"
              @click="confirmDrawTerminationAsync"
            >
              Terminate
            </button>
            <button
              v-if="isProviderMethod && mode === 'view'"
              class="secondary"
              @click="confirmModificationRecordGenerationAsync"
            >
              Generate Modification
            </button>
            <button
              v-if="canEditDraw"
              :disabled="mode === 'edit'"
              @click="changeMode('edit')"
            >
              Edit
            </button>
            <button v-if="mode !== 'view'" @click="handleCancelButtonClick">
              Cancel
            </button>
          </div>
        </PermissionsWrapper>
        <DrawForm
          ref="drawFormRef"
          :draw="selectedDraw"
          :key="selectedDraw?.id"
          :fields="formFields"
          :validationSchema="validationSchema"
          :mode="mode"
          :productTypeId="productTypeId"
          :programTypeId="programTypeId"
          :lenderId="lenderId"
          :isSubmitting="isSubmitting"
          :initialValues="selectedDraw"
          :loanId="loanId"
          :minimumDrawAmount="minimumDrawAmount"
          :loanDetails="loanDetails"
          @submit="handleSubmitAsync"
        />
      </div>

      <div v-if="mode === 'view'">
        <DrawDisbursements
          v-if="selectedDraw"
          :loanId="loanId"
          :draw="selectedDraw"
          @disbursementChangedAsync="refreshDataAsync"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, toRefs } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import functions from "@/use/functions";
import Spinner from "@/components/shared/Spinner.vue";
import DrawForm from "@/components/loan/loc/DrawForm.vue";
import StudentForm from "@/components/loan/loc/StudentForm.vue";
import DrawDisbursements from "../loc/DrawDisbursements.vue";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import {
  Draw,
  ValidDrawStatusIds,
  MinimumDrawAmountResponse,
  LoanDetails,
} from "@/models/loans";
import { useDrawFormFields } from "@/composables/loan/useDrawForm";
import { useDrawData } from "@/composables/loan/useDrawData";
import { useModificationRecord } from "@/composables/loan/useModificationRecord";
import { useDrawTermination } from "@/composables/loan/useDrawTermination";
import { useDrawActions } from "@/composables/loan/useDrawActions";
import { RootStore } from "@/types/root-store";
import store from "@/store";
import { FormChangesResponse } from "@/models/form";

const emit = defineEmits<{
  (e: "studentUpdated", personId: number): void;
}>();

const props = defineProps<{
  loanId: number;
  loanDetails: LoanDetails;
  availableToDrawAmount: number;
  minimumDrawAmount: MinimumDrawAmountResponse;
  programTypeId: number;
  productTypeId: number;
  lenderId: number;
  onDrawsChanged: () => Promise<void>;
}>();

const route = useRoute();
const router = useRouter();

const { loanDetails } = toRefs(props);

const drawFormRef = ref<InstanceType<typeof DrawForm> | null>(null);
const studentFormRef = ref<InstanceType<typeof StudentForm> | null>(null);

const isLoading = ref<boolean>(true);
const draws = ref<Draw[]>([]);
const selectedDraw = ref<Draw | null>(null);
const previousDraw = ref<Draw | null>(null);
const mode = ref<"view" | "add" | "edit">("view");
const availableToDrawAmount = ref<number>(props.availableToDrawAmount);
const minimumDrawAmount = ref<MinimumDrawAmountResponse>(
  props.minimumDrawAmount
);

const { isSubmitting, createDrawAsync, updateDrawAsync } = useDrawActions(
  props.loanId,
  refreshDataAsync
);
const { confirmDrawTerminationAsync } = useDrawTermination(
  selectedDraw,
  refreshDataAsync
);
const { isProviderMethod, confirmModificationRecordGenerationAsync } =
  useModificationRecord(selectedDraw);
const {
  certificationMethodOptions,
  drawStatusOptions,
  enrollmentTermOptions,
  schoolLevelOptions,
  getEnrollmentTermsAsync,
} = useDrawData(props.lenderId, props.productTypeId, props.programTypeId);
const { formFields, validationSchema, skipKeys, keysToRename } =
  useDrawFormFields({
    selectedDraw,
    mode,
    availableToDrawAmount,
    drawStatusOptions,
    certificationMethodOptions,
    schoolLevelOptions,
    enrollmentTermOptions,
    minimumDrawAmount,
    draws,
    loanDetails,
  });

const oidcUser = computed(() => (store.state as RootStore)?.oidcStore?.user);

const canEditDraw = computed(() => {
  if(loanDetails.value.isClosedForDraws) {
    return false;
  }

  return (
    mode.value !== "add" &&
    ValidDrawStatusIds.includes(selectedDraw.value?.drawStatusTypeId || 0)
  );
});

const canAddDraw = computed(() => {
  if(loanDetails.value.isClosedForDraws) {
    return true;
  }

  return mode.value == 'add';
})

async function refreshDataAsync() {
  await props.onDrawsChanged();
  await getDrawsAsync();

  functions.closeModal();
}

function discardChanges() {
  drawFormRef.value?.discardChanges();
  changeMode("view");
}

function handleCancelButtonClick() {
  const options = {
    keysToSkip: skipKeys,
    keysToRename: keysToRename,
  };

  const initialValues = mapDrawCompareObject(
    drawFormRef.value?.initialValuesProp
  );
  const updatedValues = mapDrawCompareObject(drawFormRef.value?.formData);

  const modalHtml = functions.checkFormChanges(
    updatedValues,
    initialValues,
    options
  );

  if (modalHtml) {
    functions.openModal({
      title: "Alert",
      description: "The changes you made have not been saved.",
      buttons: [
        {
          title: "Cancel",
          onClick: () => {
            // exits the Edit mode without saving any changes
            discardChanges();
            functions.closeModal();
          },
        },
        {
          title: "Save and Close",
          onClick: async () => {
            const result = await drawFormRef.value?.validate();
            if (!result?.valid) {
              functions.closeModal();
            } else if (drawFormRef.value?.formData) {
              confirmChanges(drawFormRef.value?.formData);
            }
          },
        },
      ],
    });
  } else {
    discardChanges();
  }
}

function mapDrawCompareObject(selectedDraw: any) {
  return {
    schoolCode: selectedDraw?.schoolCode,
    schoolBranch: selectedDraw?.schoolBranch,
    schoolName: selectedDraw?.schoolName,
    gradeLevel: selectedDraw?.schoolLevelTypeName,
    certificationMethod: selectedDraw?.certificationMethodTypeName,
    enrollmentStatus: selectedDraw?.enrollmentStatusName,
    enrollmentTerm: selectedDraw?.enrollmentTermName,
    drawStatus: selectedDraw?.drawStatusTypeName,
    requestedAmount: selectedDraw?.requestedAmountFormatted,
    certifiedAmount: selectedDraw?.certifiedAmountFormatted,
    cluid: selectedDraw.cluid as string,
    sequence: selectedDraw.sequenceNumber as string, // ignore
    anticipatedGraduationDate: selectedDraw?.anticipatedGraduationDateDisplay,
    loanPeriodStartDate: selectedDraw?.loanPeriodStartDateDisplay,
    loanPeriodEndDate: selectedDraw?.loanPeriodEndDateDisplay,
    createdBy: selectedDraw?.createdBy,
    createdAtUtc: selectedDraw.createdAtUtc,
  };
}

async function handleSubmitAsync(formData: Record<string, any>) {
  await confirmChanges(formData);
}

async function saveChangesAsync(
  formData: Record<string, any>,
  formChanges: FormChangesResponse
) {
  if (mode.value === "add") {
    await createDrawAsync(formData);

    if (draws.value.length > 0) {
      selectDraw(draws.value[draws.value.length - 1]); // select the newest draw
    }
  } else if (mode.value === "edit") {
    cleanEditRequest(formData);

    await updateDrawAsync(formData, selectedDraw.value, formChanges);
  }
}

function openConfirmChangesModal(
  modalHtml: string,
  formData: Record<string, any>,
  formChanges: FormChangesResponse
) {
  functions.openModal({
    title: "Confirm",
    description: "Please review your changes before submitting...",
    html: modalHtml,
    buttons: [
      {
        title: "Cancel",
        onClick: () => {
          // Returns back to the form
          functions.closeModal();
        },
      },
      {
        title: "Save Changes",
        onClick: async () => {
          functions.setModalLoading(true);
          await saveChangesAsync(formData, formChanges);
        },
      },
    ],
  });
}

async function confirmChanges(formData: Record<string, any>) {
  const options = {
    keysToSkip: skipKeys,
    keysToRename: keysToRename,
    commentPrefix:
      mode.value === "edit" ? `Draw Seq. ${formData.sequenceNumber} |` : null,
    userDisplayName: `${oidcUser.value.given_name} ${oidcUser.value.family_name}`,
  };

  const initialValues = mapDrawCompareObject(
    drawFormRef.value?.initialValuesProp
  );
  const updatedValues = mapDrawCompareObject(drawFormRef.value?.formData);

  const modalHtml = functions.checkFormChanges(
    updatedValues,
    initialValues,
    options
  );
  const formChanges = functions.summarizeFormChanges(
    updatedValues,
    initialValues,
    options
  );

  if (modalHtml) {
    openConfirmChangesModal(modalHtml, formData, formChanges);
  } else {
    changeMode("view");
  }
}

const changeMode = (newMode: "view" | "add" | "edit") => {
  if (newMode === "add") {
    previousDraw.value = selectedDraw.value;
    selectedDraw.value = new Draw();
  } else if (newMode === "view" && mode.value === "add") {
    if (previousDraw.value) {
      selectedDraw.value = previousDraw.value;
    } else {
      selectedDraw.value = null;
    }
  }

  mode.value = newMode;
};

const selectDraw = async (draw: Draw) => {
  selectedDraw.value = draw;

  mode.value = "view";
  router.replace({
    query: { ...route.query, drawId: draw.id },
    hash: route.hash,
  });

  const enrolmentTerms = await getEnrollmentTermsAsync(
    false,
    0,
    selectedDraw.value.schoolCode,
    selectedDraw.value.schoolBranch
  );

  if (enrolmentTerms) {
    enrollmentTermOptions.value = enrolmentTerms;
  }
};

async function getDrawsAsync() {
  try {
    const response = await axios(
      `/api/loanservice/loans/${props.loanId}/draws`
    );

    draws.value = response.data;

    if (draws.value.length > 0) {
      selectInitialDraw();
    }
  } catch (error) {
    functions.openModal({
      title: "Oops!",
      description:
        "There was an issue getting the draws. Please try again later.",
    });
  }
}

const selectInitialDraw = () => {
  const queryDrawId = route.query.drawId;

  if (queryDrawId) {
    const matchingDraw = draws.value.find(
      (draw) => draw.id === Number(queryDrawId)
    );

    if (matchingDraw) {
      selectDraw(matchingDraw);
    } else {
      selectDraw(draws.value[0]); // default to first if not in list
    }
  } else if (draws.value.length > 0) {
    selectDraw(draws.value[0]); // default to first if not in query
  }
};

function cleanEditRequest(formData: Record<string, any>) {
  // set loan period start/end date(s) to null if there is no change,
  // in order to bypass the date validation in the request object
  if (
    selectedDraw.value?.loanPeriodStartDate !== null &&
    selectedDraw.value?.loanPeriodStartDate === formData["loanPeriodStartDate"]
  ) {
    formData["loanPeriodStartDate"] = null;
  }

  if (
    selectedDraw.value?.loanPeriodEndDate !== null &&
    selectedDraw.value?.loanPeriodEndDate === formData["loanPeriodEndDate"]
  ) {
    formData["loanPeriodEndDate"] = null;
  }

  if (
    selectedDraw.value?.createdAtUtc !== null &&
    selectedDraw.value?.createdAtUtc === formData["createdAtUtc"]
  ) {
    formData["createdAtUtc"] = null;
  }
}

onMounted(async () => {
  await getDrawsAsync();

  isLoading.value = false;
});

defineExpose({
  studentFormRef,
});
</script>
<style scoped src="@/assets/css/card.css"></style>
<style scoped src="@/assets/css/loans/draw-disbursement-details.css"></style>
